import { RefObject, useEffect, useRef } from 'react'

type Params<T extends HTMLElement> = {
  onTriggered: (event: MouseEvent | TouchEvent) => void
  disabled?: boolean
  initialRef?: RefObject<T | null>
}

export function useDetectClickOutside<T extends HTMLElement = HTMLElement>({
  onTriggered,
  disabled = false,
  initialRef,
}: Params<T>) {
  const fallbackRef = useRef<T | null>(null)
  const ref = initialRef ?? fallbackRef

  useEffect(() => {
    if (disabled) return

    function handleEvent(event: MouseEvent | TouchEvent) {
      const element = ref.current
      if (!element || element.contains(event.target as Node)) return
      onTriggered(event)
    }

    document.addEventListener('mousedown', handleEvent)
    document.addEventListener('touchstart', handleEvent)

    return () => {
      document.removeEventListener('mousedown', handleEvent)
      document.removeEventListener('touchstart', handleEvent)
    }
  }, [disabled, onTriggered, ref])

  return ref
}
